import { useCallback, useEffect, useMemo, useState } from "react";
import { http, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { ReceiptButton } from "@/components/ReceiptButton";
import SearchableSelect from "@/components/SearchableSelect";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    ForkKnife,
    Car,
    Bed,
    Package,
    DotsThreeCircle,
    Plus,
    Trash,
    PencilSimple,
    Receipt,
    DownloadSimple,
    CheckCircle,
    Circle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
    { v: "dieta", l: "Dieta / manutención", Icon: ForkKnife },
    { v: "desplazamiento", l: "Desplazamiento", Icon: Car },
    { v: "alojamiento", l: "Alojamiento", Icon: Bed },
    { v: "material", l: "Material", Icon: Package },
    { v: "otro", l: "Otro", Icon: DotsThreeCircle },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.v, c]));

const DEFAULT_KM_RATE = 0.45; // €/km por defecto para desplazamientos

const eur = (n) =>
    new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    }).format(Number(n) || 0);

function monthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
        from: start.toISOString().slice(0, 10),
        to: end.toISOString().slice(0, 10),
    };
}

const emptyForm = {
    project_id: "",
    expense_date: new Date().toISOString().slice(0, 10),
    category: "dieta",
    amount: 0,
    currency: "EUR",
    km: "",
    description: "",
};

export default function Expenses() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const range = monthRange();
    const [dateFrom, setDateFrom] = useState(range.from);
    const [dateTo, setDateTo] = useState(range.to);
    const [projectFilter, setProjectFilter] = useState("all");
    const [allUsers, setAllUsers] = useState(false);
    const [projects, setProjects] = useState([]);
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ total: 0, by_category: {}, count: 0 });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [kmRate, setKmRate] = useState(DEFAULT_KM_RATE);

    const load = useCallback(async () => {
        const params = { date_from: dateFrom, date_to: dateTo };
        if (projectFilter !== "all") params.project_id = projectFilter;
        if (allUsers && isAdmin) params.all_users = true;
        const [ex, sum] = await Promise.all([
            http.get("/expenses", { params }),
            http.get("/expenses/summary", { params }),
        ]);
        setItems(ex.data);
        setSummary(sum.data);
    }, [dateFrom, dateTo, projectFilter, allUsers, isAdmin]);

    useEffect(() => {
        http.get("/projects").then((r) => setProjects(r.data));
        http.get("/settings").then((r) => {
            if (r.data?.km_rate != null) setKmRate(Number(r.data.km_rate));
        });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const activeProjects = useMemo(
        () => projects.filter((p) => p.active !== false),
        [projects],
    );

    const submit = async (e) => {
        e.preventDefault();
        try {
            const kmVal = form.km === "" || form.km == null ? null : Number(form.km);
            const payload = {
                ...form,
                amount: Number(form.amount),
                km:
                    form.category === "desplazamiento" && kmVal !== null && !Number.isNaN(kmVal)
                        ? kmVal
                        : null,
                description: form.description || null,
            };
            if (form.expense_id) {
                await http.put(`/expenses/${form.expense_id}`, payload);
                toast.success("Gasto actualizado");
            } else {
                await http.post("/expenses", payload);
                toast.success("Gasto registrado");
            }
            setOpen(false);
            setForm(emptyForm);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const edit = (e) => {
        setForm({ ...emptyForm, ...e });
        setOpen(true);
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar este gasto?")) return;
        await http.delete(`/expenses/${id}`);
        toast.success("Eliminado");
        load();
    };

    const csvUrl = () => {
        const params = new URLSearchParams();
        params.set("date_from", dateFrom);
        params.set("date_to", dateTo);
        if (projectFilter !== "all") params.set("project_id", projectFilter);
        if (allUsers && isAdmin) params.set("all_users", "true");
        return `${API}/expenses/export.csv?${params.toString()}`;
    };

    const accessors = useMemo(
        () => ({
            date: (e) => e.expense_date || "",
            project: (e) => e.project_name || "",
            user: (e) => e.user_name || "",
            category: (e) => e.category || "",
            km: (e) => Number(e.km || 0),
            description: (e) => e.description || "",
            amount: (e) => Number(e.amount || 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        items,
        "expenses",
        accessors,
        { initialKey: "date", initialDir: "desc" },
    );

    const catEntries = Object.entries(summary.by_category || {}).sort(
        (a, b) => b[1] - a[1],
    );

    return (
        <div data-testid="page-expenses">
            <PageHeader
                eyebrow="Gastos y dietas"
                title="Gastos por proyecto"
                description="Registra dietas, desplazamientos y otros gastos imputados a un proyecto."
                actions={
                    <div className="flex gap-2">
                        <a
                            href={csvUrl()}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm text-slate-700"
                            data-testid="expenses-csv"
                        >
                            <DownloadSimple size={14} /> Exportar CSV
                        </a>
                        <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                onClick={() => setForm(emptyForm)}
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                data-testid="expense-new-btn"
                            >
                                <Plus size={14} className="mr-2" /> Nuevo gasto
                            </Button>
                        </SheetTrigger>
                        <SheetContent className="rounded-none sm:max-w-md">
                            <SheetHeader>
                                <SheetTitle className="font-display">
                                    {form.expense_id ? "Editar gasto" : "Nuevo gasto"}
                                </SheetTitle>
                                <SheetDescription className="text-xs text-slate-500">
                                    Los gastos se asocian a un proyecto activo.
                                </SheetDescription>
                            </SheetHeader>
                            <form
                                onSubmit={submit}
                                className="mt-6 space-y-3"
                                data-testid="expense-form"
                            >
                                <div>
                                    <Label className="text-xs">Proyecto</Label>
                                    <SearchableSelect
                                        value={form.project_id}
                                        onChange={(v) =>
                                            setForm({ ...form, project_id: v })
                                        }
                                        options={activeProjects.map((p) => ({
                                            value: p.project_id,
                                            label: p.name,
                                            hint: p.project_code || "",
                                        }))}
                                        placeholder="Selecciona proyecto…"
                                        allowClear={false}
                                        className="mt-1"
                                        data-testid="expense-project"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Fecha</Label>
                                        <Input
                                            type="date"
                                            required
                                            value={form.expense_date}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    expense_date: e.target.value,
                                                })
                                            }
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                            data-testid="expense-date"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Importe (€)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            required
                                            readOnly={form.category === "desplazamiento"}
                                            value={form.amount}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    amount: e.target.value,
                                                })
                                            }
                                            className={`mt-1 rounded-sm border-slate-300 font-mono ${
                                                form.category === "desplazamiento"
                                                    ? "bg-slate-100 text-slate-600"
                                                    : ""
                                            }`}
                                            data-testid="expense-amount"
                                        />
                                        {form.category === "desplazamiento" && (
                                            <p className="text-[11px] text-slate-500 mt-1">
                                                Calculado automáticamente
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs">Categoría</Label>
                                    <Select
                                        value={form.category}
                                        onValueChange={(v) => {
                                            if (v === "desplazamiento") {
                                                const kmNum = Number(form.km);
                                                const amt =
                                                    form.km !== "" &&
                                                    form.km != null &&
                                                    !Number.isNaN(kmNum)
                                                        ? (kmNum * kmRate).toFixed(2)
                                                        : 0;
                                                setForm({ ...form, category: v, amount: amt });
                                            } else {
                                                setForm({ ...form, category: v });
                                            }
                                        }}
                                    >
                                        <SelectTrigger
                                            className="mt-1 rounded-sm border-slate-300"
                                            data-testid="expense-category"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map((c) => (
                                                <SelectItem key={c.v} value={c.v}>
                                                    {c.l}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {form.category === "desplazamiento" && (
                                    <div>
                                        <Label className="text-xs">Km recorridos (coche)</Label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={form.km ?? ""}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const kmNum = Number(v);
                                                const amt =
                                                    v !== "" && !Number.isNaN(kmNum)
                                                        ? (kmNum * kmRate).toFixed(2)
                                                        : 0;
                                                setForm({ ...form, km: v, amount: amt });
                                            }}
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                            placeholder="Ej. 82.5"
                                            data-testid="expense-km"
                                        />
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            Solo para desplazamientos en coche. El importe se
                                            calcula automáticamente a {kmRate.toFixed(2)} €/km.
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs">Descripción (opcional)</Label>
                                    <Textarea
                                        rows={2}
                                        value={form.description || ""}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                description: e.target.value,
                                            })
                                        }
                                        className="mt-1 rounded-sm border-slate-300"
                                        placeholder="Comida con cliente, taxi al aeropuerto…"
                                        data-testid="expense-description"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="expense-save"
                                >
                                    {form.expense_id ? "Guardar" : "Registrar"}
                                </Button>
                            </form>
                        </SheetContent>
                    </Sheet>
                    </div>
                }
            />
            <div className="p-6 lg:p-10 space-y-6">
                <div className="border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mr-2">
                        Filtros
                    </span>
                    <div>
                        <Label className="text-xs">Desde</Label>
                        <Input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="mt-1 rounded-sm border-slate-300 font-mono h-8"
                            data-testid="expense-filter-from"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Hasta</Label>
                        <Input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="mt-1 rounded-sm border-slate-300 font-mono h-8"
                            data-testid="expense-filter-to"
                        />
                    </div>
                    <div className="min-w-[220px]">
                        <Label className="text-xs">Proyecto</Label>
                        <SearchableSelect
                            value={projectFilter === "all" ? "" : projectFilter}
                            onChange={(v) => setProjectFilter(v || "all")}
                            options={activeProjects.map((p) => ({
                                value: p.project_id,
                                label: p.name,
                                hint: p.project_code || "",
                            }))}
                            placeholder="Todos"
                            className="mt-1 h-8 !py-1.5"
                            data-testid="expense-filter-project"
                        />
                    </div>
                    {isAdmin && (
                        <div className="flex items-center gap-2 h-8 mt-5">
                            <input
                                type="checkbox"
                                id="all-users"
                                checked={allUsers}
                                onChange={(e) => setAllUsers(e.target.checked)}
                                data-testid="expense-filter-all-users"
                            />
                            <Label
                                htmlFor="all-users"
                                className="text-xs cursor-pointer"
                            >
                                Todos los empleados
                            </Label>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                    <div className="bg-white p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Total periodo
                        </div>
                        <div
                            className="font-mono text-3xl font-bold mt-2 text-slate-950"
                            data-testid="expense-total"
                        >
                            {eur(summary.total)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            {summary.count} gasto{summary.count === 1 ? "" : "s"}
                        </div>
                    </div>
                    <div className="bg-white p-6 lg:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                            Por categoría
                        </div>
                        <div className="space-y-2">
                            {catEntries.length === 0 && (
                                <div className="text-sm text-slate-400">Sin datos.</div>
                            )}
                            {catEntries.map(([k, v]) => {
                                const cat = CAT_MAP[k];
                                const pct =
                                    summary.total > 0 ? (v / summary.total) * 100 : 0;
                                const Icon = cat?.Icon || DotsThreeCircle;
                                return (
                                    <div
                                        key={k}
                                        className="flex items-center gap-3 text-sm"
                                    >
                                        <Icon
                                            size={14}
                                            className="text-slate-500 shrink-0"
                                        />
                                        <span className="w-40 truncate text-slate-700">
                                            {cat?.l || k}
                                        </span>
                                        <div className="flex-1 h-1.5 bg-slate-100 relative">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-[#002BAF]"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <span className="font-mono tabular-nums text-slate-900 font-semibold w-24 text-right">
                                            {eur(v)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="expenses-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("date", "py-3 px-4 text-left")}>
                                    Fecha
                                    <SortIndicator active={sortKey === "date"} dir={sortDir} />
                                </th>
                                <th {...headerProps("project", "py-3 px-4 text-left")}>
                                    Proyecto
                                    <SortIndicator active={sortKey === "project"} dir={sortDir} />
                                </th>
                                {allUsers && isAdmin && (
                                    <th {...headerProps("user", "py-3 px-4 text-left")}>
                                        Empleado
                                        <SortIndicator
                                            active={sortKey === "user"}
                                            dir={sortDir}
                                        />
                                    </th>
                                )}
                                <th {...headerProps("category", "py-3 px-4 text-left")}>
                                    Categoría
                                    <SortIndicator
                                        active={sortKey === "category"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("km", "py-3 px-4 text-right")}>
                                    Km
                                    <SortIndicator active={sortKey === "km"} dir={sortDir} />
                                </th>
                                <th {...headerProps("description", "py-3 px-4 text-left")}>
                                    Descripción
                                    <SortIndicator
                                        active={sortKey === "description"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("amount", "py-3 px-4 text-right")}>
                                    Importe
                                    <SortIndicator
                                        active={sortKey === "amount"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th className="py-3 px-4 text-center">Estado</th>
                                <th className="py-3 px-4 text-center">Justificante</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={allUsers && isAdmin ? 10 : 9}
                                        className="text-center py-12 text-slate-500"
                                    >
                                        <Receipt
                                            size={28}
                                            className="mx-auto text-slate-300 mb-3"
                                        />
                                        No hay gastos registrados en este periodo.
                                    </td>
                                </tr>
                            )}
                            {sorted.map((e) => {
                                const cat = CAT_MAP[e.category];
                                const Icon = cat?.Icon || DotsThreeCircle;
                                return (
                                    <tr
                                        key={e.expense_id}
                                        className="border-b border-slate-100 hover:bg-slate-50"
                                    >
                                        <td className="py-3 px-4 font-mono text-slate-700">
                                            {e.expense_date}
                                        </td>
                                        <td className="py-3 px-4 text-slate-800">
                                            <div className="font-medium truncate max-w-xs">
                                                {e.project_name}
                                            </div>
                                            {e.project_code && (
                                                <div className="text-[11px] text-slate-400 font-mono">
                                                    {e.project_code}
                                                </div>
                                            )}
                                        </td>
                                        {allUsers && isAdmin && (
                                            <td className="py-3 px-4 text-slate-700">
                                                {e.user_name}
                                            </td>
                                        )}
                                        <td className="py-3 px-4">
                                            <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                                                <Icon size={12} />
                                                {cat?.l || e.category}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-700">
                                            {e.km != null && e.km !== ""
                                                ? Number(e.km).toLocaleString("es-ES", {
                                                      maximumFractionDigits: 2,
                                                  })
                                                : "—"}
                                        </td>
                                        <td className="py-3 px-4 text-slate-600 max-w-sm truncate">
                                            {e.description || "—"}
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-900 font-semibold">
                                            {eur(e.amount)}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <span
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border ${
                                                    e.paid
                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                        : "bg-amber-50 text-amber-700 border-amber-200"
                                                }`}
                                                data-testid={`expense-paid-status-${e.expense_id}`}
                                            >
                                                {e.paid ? (
                                                    <CheckCircle size={14} weight="fill" />
                                                ) : (
                                                    <Circle size={14} />
                                                )}
                                                {e.paid ? "Pagado" : "Pendiente"}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <ReceiptButton
                                                expense={e}
                                                onUpdated={() => load()}
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => edit(e)}
                                                className="text-slate-500 hover:text-slate-900 mr-3"
                                                data-testid={`expense-edit-${e.expense_id}`}
                                            >
                                                <PencilSimple size={16} />
                                            </button>
                                            <button
                                                onClick={() => remove(e.expense_id)}
                                                className="text-slate-400 hover:text-red-600"
                                                data-testid={`expense-remove-${e.expense_id}`}
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
